import type NativeArray from '@ember/array/-private/native-array';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type { AsyncHasMany } from '@ember-data/model';
import Component from '@glimmer/component';
import type AbilitiesService from 'ember-can/services/abilities';
import type { TaskInstance } from 'ember-concurrency';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import type Location from 'garaje/models/location';
import type User from 'garaje/models/user';
import type BlocklistFilterService from 'garaje/services/blocklist-filter';
import type CurrentLocationService from 'garaje/services/current-location';
import type StateService from 'garaje/services/state';
import type { RecordArray } from 'garaje/utils/type-utils';
import { Permission } from 'garaje/utils/ui-permissions';
import { all } from 'rsvp';

interface SecurityComponentsConfigureBlocklistLocationSignature {
  Args: {
    /**
     * whether the location is connected to a multi-tenant property
     */
    isConnectedToProperty: boolean;
  };
}

export default class SecurityComponentsConfigureBlocklistLocation extends Component<SecurityComponentsConfigureBlocklistLocationSignature> {
  @service declare abilities: AbilitiesService;
  @service('blocklistFilter') declare blocklistFilterService: BlocklistFilterService;
  @service declare currentLocation: CurrentLocationService;
  @service declare state: StateService;

  determineAdminPermissionText =
    'Adding admins to the above alert list will give them permission to approve and deny visitors.';

  get canCreateBlocklist(): boolean {
    return this.abilities.can('create blocklist in security');
  }

  get canUpdateBlocklist(): boolean {
    return this.abilities.can('update blocklist in security');
  }

  get canUploadBlocklist(): boolean {
    return this.abilities.can('upload blocklist in security');
  }

  @action
  loadBlocklistContacts(): AsyncHasMany<User> {
    return this.state.currentLocation.blocklistContacts;
  }

  @action
  loadBlocklistFilters(): TaskInstance<RecordArray<BlacklistFilter>> {
    return this.blocklistFilterService.queryByCompanyTask.perform(this.state.currentCompany.id);
  }

  @action
  async saveBlocklistContacts(blocklistContacts: NativeArray<User>): Promise<Location> {
    const contacts = await this.state.currentLocation.blocklistContacts;
    contacts.clear();
    contacts.pushObjects(blocklistContacts);

    return this.state.currentLocation.save();
  }

  @action
  async searchUsers(term: string): Promise<User[]> {
    const roles = ['Global Admin', 'Location Admin', 'Receptionist'];
    const permissions = [
      Permission.VISITORS_ENTRY_BLOCKLIST_APPROVAL_REVIEW,
      Permission.VISITORS_INVITE_BLOCKLIST_APPROVAL_REVIEW,
    ];
    const confirmedUsers = true;
    const userRoles = await this.currentLocation.searchUsers.perform({ term, roles, permissions, confirmedUsers });
    return all(userRoles.map((role) => role.user));
  }
}
